import { useEffect, useMemo, useRef, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import FormDate from "../../Form/FormDate";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import {
  addLocalNotification,
  clearBusinessFixedAsset,
  clearBusinessFixedAssetError,
  requestCreateBusinessFixedAsset,
  requestListAccount,
  requestUpdateBusinessFixedAsset,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessFixedAssetDispatch,
  useBusinessFixedAssetState,
  useNotificationDispatch,
} from "../../../../Context";
import { cancelAPIRequest } from "../../../../Utils";

const DEFAULT_EXPENSE_CODE = "660217";
const DEFAULT_FUNDING_CODE = "110001";

// Which chart-of-account rows each picker offers. Accumulated depreciation is a
// contra-asset: the default chart files it under FIXED_ASSETS with type CREDIT
// (110751-110757 "Akumulasi Penyusutan"), so match those as well as any account
// on the dedicated DEPRECIATION_AND_AMORTIZATION category. The cost and expense
// pickers stay on DEBIT rows so a contra account never shows up there.
const isAssetAccount = (a) =>
  ["FIXED_ASSETS", "OTHER_ASSETS"].includes(a.category) && a.type === "DEBIT";
const isAccumulatedAccount = (a) =>
  a.category === "DEPRECIATION_AND_AMORTIZATION" ||
  (a.category === "FIXED_ASSETS" && a.type === "CREDIT");
const isExpenseAccount = (a) =>
  ["EXPENSE", "OTHER_EXPENSE"].includes(a.category) && a.type === "DEBIT";
const FUNDING_CATEGORIES = [
  "CASH_AND_BANK",
  "ACCOUNTS_PAYABLE",
  "OTHER_CURRENT_LIABILITIES",
  "LONG_TERM_OBLIGATION",
];
const isFundingAccount = (a) => FUNDING_CATEGORIES.includes(a.category);

const FixedAssetModal = ({ open, onClose, asset }) => {
  const { t } = useTranslation();

  const isEdit = Boolean(asset);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [acquisitionCost, setAcquisitionCost] = useState("");
  const [residualValue, setResidualValue] = useState("");
  const [usefulLifeMonths, setUsefulLifeMonths] = useState("");
  const [acquisitionDate, setAcquisitionDate] = useState(new Date());
  const [depreciationStartDate, setDepreciationStartDate] = useState(
    new Date(),
  );
  const [assetAccount, setAssetAccount] = useState(null);
  const [accumAccount, setAccumAccount] = useState(null);
  const [expenseAccount, setExpenseAccount] = useState(null);
  const [fundingAccount, setFundingAccount] = useState(null);
  const [skipJournal, setSkipJournal] = useState(false);
  const [errors, setErrors] = useState({});

  const notificationDispatch = useNotificationDispatch();
  const accountingDispatch = useAccountingDispatch();
  const dispatch = useBusinessFixedAssetDispatch();

  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const { accounts, allAccountLoading, accountLoading, accountError } =
    useAccountingState();
  const {
    request,
    newFixedAsset,
    newFixedAssetLoading,
    newFixedAssetError,
    fixedAssetID: editedID,
    updateFixedAssetLoading,
    updateFixedAssetError,
  } = useBusinessFixedAssetState();

  const submitLoading = newFixedAssetLoading || updateFixedAssetLoading;
  const accountsLoading = allAccountLoading || accountLoading;

  // Load the whole chart of accounts once so each picker can filter locally.
  useEffect(() => {
    if (user?.mainBusiness?.id) {
      requestListAccount(accountingDispatch, {
        page: 1,
        businessID: user.mainBusiness.id,
        limit: 200,
        loadAll: true,
      });
    }
  }, [accountingDispatch, user?.mainBusiness?.id]);

  // Clean up on unmount only. With `request` as a dependency this cleanup also
  // ran when a failed save reset `request` to null, wiping the error before it
  // was shown, and cancelled whatever request the page had in flight.
  const requestRef = useRef(request);
  requestRef.current = request;
  useEffect(() => {
    return () => {
      cancelAPIRequest(requestRef.current);
      clearBusinessFixedAsset(dispatch);
      clearBusinessFixedAssetError(dispatch);
    };
  }, [dispatch]);

  const optionsFor = (predicate) =>
    (accounts || [])
      .filter(predicate)
      .slice()
      .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10))
      .map((acc) => ({
        value: acc.code,
        label: `${acc.code} - ${acc.name}`,
      }));

  const assetOptions = useMemo(
    () => optionsFor(isAssetAccount),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );
  const accumOptions = useMemo(
    () => optionsFor(isAccumulatedAccount),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );
  const expenseOptions = useMemo(
    () => optionsFor(isExpenseAccount),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );
  const fundingOptions = useMemo(
    () => optionsFor(isFundingAccount),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );

  const financialLocked =
    isEdit && Number(asset?.accumulatedDepreciation || 0) > 0;

  // Populate when editing, or apply sensible defaults when creating.
  useEffect(() => {
    if (isEdit && asset) {
      setName(asset.name || "");
      setDescription(asset.description || "");
      setAcquisitionCost(String(asset.acquisitionCost ?? ""));
      setResidualValue(String(asset.residualValue ?? ""));
      setUsefulLifeMonths(String(asset.usefulLifeMonths ?? ""));
      if (asset.acquisitionDate)
        setAcquisitionDate(dayjs(asset.acquisitionDate).toDate());
      if (asset.depreciationStartDate)
        setDepreciationStartDate(dayjs(asset.depreciationStartDate).toDate());
    }
  }, [isEdit, asset]);

  // Resolve the code-based account selections once options are available.
  useEffect(() => {
    if (isEdit && asset) {
      const findBy = (options, code) =>
        options.find((o) => o.value === code) || null;
      if (assetOptions.length)
        setAssetAccount(findBy(assetOptions, asset.assetAccountCode));
      if (accumOptions.length)
        setAccumAccount(findBy(accumOptions, asset.accumulatedAccountCode));
      if (expenseOptions.length)
        setExpenseAccount(findBy(expenseOptions, asset.expenseAccountCode));
    } else {
      // Default the expense account to 660217 Beban Penyusutan & Amortisasi
      // and the funding account to 110001 Kas.
      if (expenseOptions.length && !expenseAccount) {
        const preset = expenseOptions.find(
          (o) => o.value === DEFAULT_EXPENSE_CODE,
        );
        if (preset) setExpenseAccount(preset);
      }
      if (fundingOptions.length && !fundingAccount) {
        const preset = fundingOptions.find(
          (o) => o.value === DEFAULT_FUNDING_CODE,
        );
        if (preset) setFundingAccount(preset);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assetOptions,
    accumOptions,
    expenseOptions,
    fundingOptions,
    isEdit,
    asset,
  ]);

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const validate = () => {
    const e = {};
    if (!name) e.name = "fixedAssets.errors.nameRequired";
    if (!acquisitionCost || Number(acquisitionCost) <= 0)
      e.acquisitionCost = "fixedAssets.errors.acquisitionCostRequired";
    if (residualValue && Number(residualValue) < 0)
      e.residualValue = "fixedAssets.errors.residualNegative";
    if (
      residualValue &&
      acquisitionCost &&
      Number(residualValue) >= Number(acquisitionCost)
    )
      e.residualValue = "fixedAssets.errors.residualTooHigh";
    if (!usefulLifeMonths || parseInt(usefulLifeMonths, 10) <= 0)
      e.usefulLifeMonths = "fixedAssets.errors.usefulLifeRequired";
    if (!assetAccount?.value)
      e.assetAccount = "fixedAssets.errors.assetAccountRequired";
    if (!accumAccount?.value)
      e.accumAccount = "fixedAssets.errors.accumAccountRequired";
    if (!expenseAccount?.value)
      e.expenseAccount = "fixedAssets.errors.expenseAccountRequired";
    // Only the acquisition journal needs a funding account, and only on create.
    if (!isEdit && !skipJournal && !fundingAccount?.value)
      e.fundingAccount = "fixedAssets.errors.fundingAccountRequired";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (writeBlocked) return;
    if (!validate()) return;

    const input = {
      businessID: user?.mainBusiness?.id,
      name,
      description: description || null,
      assetAccountCode: assetAccount.value,
      accumulatedAccountCode: accumAccount.value,
      expenseAccountCode: expenseAccount.value,
      acquisitionCost: Number(acquisitionCost),
      residualValue: Number(residualValue || 0),
      usefulLifeMonths: parseInt(usefulLifeMonths, 10),
      method: "STRAIGHT_LINE",
      acquisitionDate: dayjs(acquisitionDate).toISOString(),
      depreciationStartDate: dayjs(
        depreciationStartDate || acquisitionDate,
      ).toISOString(),
    };

    if (isEdit) {
      input.id = asset.id;
      requestUpdateBusinessFixedAsset(dispatch, { input });
    } else {
      input.skipJournal = skipJournal;
      if (!skipJournal) input.fundingAccountCode = fundingAccount.value;
      requestCreateBusinessFixedAsset(dispatch, { input });
    }
  };

  // Create success
  useEffect(() => {
    if (newFixedAsset) {
      onClose();
      addLocalNotification(notificationDispatch, {
        id: `create-fixed-asset-${newFixedAsset?.id || Date.now()}`,
        type: "success",
        title: "common.success",
        body: "fixedAssets.notifications.createSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, newFixedAsset, onClose]);

  // Update success
  useEffect(() => {
    if (editedID) {
      onClose();
      addLocalNotification(notificationDispatch, {
        id: `update-fixed-asset-${editedID}`,
        type: "success",
        title: "common.success",
        body: "fixedAssets.notifications.updateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, editedID, onClose]);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={
        isEdit ? "fixedAssets.modal.title.edit" : "fixedAssets.modal.title.add"
      }
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={writeBlocked || submitLoading}
            loading={submitLoading}
            type="submit"
            form="fixed-asset-form"
            variant="primary"
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </div>
      }
    >
      <form id="fixed-asset-form" className="space-y-4" onSubmit={handleSubmit}>
        <WriteBlockGate>
          <FormError error={newFixedAssetError || updateFixedAssetError} />
          <Fieldset
            disabled={submitLoading}
            className="space-y-4 data-[disabled]:opacity-50"
          >
            <Field>
              <FormInput
                required
                label="fixedAssets.fields.name"
                id="asset-name"
                name="asset_name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clearError("name");
                }}
                placeholder={t("fixedAssets.placeholders.name")}
                error={errors.name}
              />
            </Field>

            <Field>
              <FormInput
                label="fixedAssets.fields.description"
                id="asset-description"
                name="asset_description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("fixedAssets.placeholders.description")}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field>
                <FormInput
                  required
                  label="fixedAssets.fields.acquisitionCost"
                  id="acquisition-cost"
                  name="acquisition_cost"
                  type="number"
                  min="0"
                  prefix="Rp"
                  disabled={financialLocked}
                  value={acquisitionCost}
                  onChange={(e) => {
                    setAcquisitionCost(e.target.value);
                    clearError("acquisitionCost");
                  }}
                  placeholder={t("fixedAssets.placeholders.acquisitionCost")}
                  error={errors.acquisitionCost}
                />
              </Field>
              <Field>
                <FormInput
                  label="fixedAssets.fields.residualValue"
                  id="residual-value"
                  name="residual_value"
                  type="number"
                  min="0"
                  prefix="Rp"
                  disabled={financialLocked}
                  value={residualValue}
                  onChange={(e) => {
                    setResidualValue(e.target.value);
                    clearError("residualValue");
                  }}
                  placeholder={t("fixedAssets.placeholders.residualValue")}
                  error={errors.residualValue}
                />
              </Field>
            </div>

            <Field>
              <FormInput
                required
                label="fixedAssets.fields.usefulLifeMonths"
                hint={t("fixedAssets.hints.usefulLifeMonths")}
                id="useful-life"
                name="useful_life"
                type="number"
                min="1"
                disabled={financialLocked}
                value={usefulLifeMonths}
                onChange={(e) => {
                  setUsefulLifeMonths(e.target.value);
                  clearError("usefulLifeMonths");
                }}
                placeholder={t("fixedAssets.placeholders.usefulLifeMonths")}
                error={errors.usefulLifeMonths}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormDate
                label="fixedAssets.fields.acquisitionDate"
                disabled={financialLocked}
                selected={acquisitionDate}
                onChange={(date) => setAcquisitionDate(date)}
              />
              <FormDate
                label="fixedAssets.fields.depreciationStartDate"
                disabled={financialLocked}
                selected={depreciationStartDate}
                onChange={(date) => setDepreciationStartDate(date)}
              />
            </div>

            <Field>
              <FormSelect
                required
                searchable
                label="fixedAssets.fields.assetAccount"
                id="asset-account"
                name="asset_account"
                options={assetOptions}
                isLoading={accountsLoading}
                disabled={financialLocked}
                value={assetAccount}
                onChange={(opt) => {
                  setAssetAccount(opt);
                  clearError("assetAccount");
                }}
                placeholder={t("fixedAssets.placeholders.assetAccount")}
                error={errors.assetAccount || accountError}
              />
            </Field>

            <Field>
              <FormSelect
                required
                searchable
                label="fixedAssets.fields.accumulatedAccount"
                id="accum-account"
                name="accum_account"
                options={accumOptions}
                isLoading={accountsLoading}
                disabled={financialLocked}
                value={accumAccount}
                onChange={(opt) => {
                  setAccumAccount(opt);
                  clearError("accumAccount");
                }}
                placeholder={t("fixedAssets.placeholders.accumulatedAccount")}
                error={errors.accumAccount}
              />
            </Field>

            <Field>
              <FormSelect
                required
                searchable
                label="fixedAssets.fields.expenseAccount"
                id="expense-account"
                name="expense_account"
                options={expenseOptions}
                isLoading={accountsLoading}
                disabled={financialLocked}
                value={expenseAccount}
                onChange={(opt) => {
                  setExpenseAccount(opt);
                  clearError("expenseAccount");
                }}
                placeholder={t("fixedAssets.placeholders.expenseAccount")}
                error={errors.expenseAccount}
              />
            </Field>

            {!isEdit && (
              <>
                <Field>
                  <FormSelect
                    required
                    searchable
                    label="fixedAssets.fields.fundingAccount"
                    id="funding-account"
                    name="funding_account"
                    options={fundingOptions}
                    isLoading={accountsLoading}
                    disabled={skipJournal}
                    value={fundingAccount}
                    onChange={(opt) => {
                      setFundingAccount(opt);
                      clearError("fundingAccount");
                    }}
                    placeholder={t("fixedAssets.placeholders.fundingAccount")}
                    error={errors.fundingAccount}
                  />
                </Field>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skip-journal"
                    checked={skipJournal}
                    onChange={(event) => {
                      setSkipJournal(event.target.checked);
                      clearError("fundingAccount");
                    }}
                    className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="skip-journal"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    {t("fixedAssets.fields.acquisitionAlreadyRecorded")}
                  </label>
                </div>
              </>
            )}
          </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default FixedAssetModal;
